import { createContext, type CSSProperties, useContext, useEffect, useRef, useState } from 'react';

/** The viewer asked for less motion (OS setting). Safe outside a browser. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  // jsdom and old browsers have no matchMedia.
  const query = (window as Partial<Window>).matchMedia;
  return query?.call(window, '(prefers-reduced-motion: reduce)').matches ?? false;
}

/**
 * A page may play its entrance: a browser that can say whether motion is wanted (jsdom cannot, so unit tests
 * see the final state), animation frames, motion not reduced, and the tab visible.
 */
export function canAnimate(): boolean {
  if (typeof window === 'undefined' || typeof requestAnimationFrame !== 'function') return false;
  if (typeof (window as Partial<Window>).matchMedia !== 'function') return false;
  if (typeof document !== 'undefined' && document.hidden) return false;
  return !prefersReducedMotion();
}

/**
 * Where a page's entrance is (design v3): `entered` turns true two frames after the first paint, so bars,
 * shapes and cells grow from their empty state; `settled` once the staggered choreography is over, after
 * which changes move quickly and without delays. Outside an entrance both are true.
 */
export interface EntranceState {
  entered: boolean;
  settled: boolean;
}
export const EntranceContext = createContext<EntranceState>({ entered: true, settled: true });
/** Eased 0 → 1 over the entrance (rings sweep, numbers count up); 1 outside one. */
export const EntranceProgress = createContext(1);

export const useEntrance = () => useContext(EntranceContext);
export const useEntranceProgress = () => useContext(EntranceProgress);

/** `n` counted up from 0 during the entrance. Text alternatives should keep using `n`. */
export function useCountUp(n: number): number {
  const k = useEntranceProgress();
  return k >= 1 ? n : Math.round(n * k);
}

/**
 * Gap between two pieces of a stagger: `step` ms, shortened when there are many, so the last one starts
 * within `span` ms and the entrance ends on time whatever the count.
 */
export const staggerStep = (count: number, step: number, span = 900) =>
  Math.min(step, span / Math.max(1, count));

/** Delay before a staggered piece moves: `ms` during the entrance, none once settled. */
export const entranceDelay = (e: EntranceState, ms: number) => (e.settled ? '0ms' : `${Math.round(ms)}ms`);
/** Long, smooth moves during the entrance; quick ones after it. */
export const entranceDuration = (e: EntranceState, ms = 900) => (e.settled ? '0.3s' : `${ms}ms`);

/**
 * `--dl` and `--dur` for a CSS transition written as `var(--dur, …) … var(--dl, 0ms)`: the piece waits
 * `delayMs` and moves for `durationMs` during the entrance, then reacts at once and quickly.
 */
export function entranceVars(e: EntranceState, delayMs: number, durationMs = 900): CSSProperties {
  // Settled: nothing inline, the stylesheet's quick defaults apply.
  if (e.settled) return {};
  return {
    ['--dl' as string]: entranceDelay(e, delayMs),
    ['--dur' as string]: entranceDuration(e, durationMs),
  };
}

/** Ease-out cubic: fast start, gentle stop. */
export const easeOut = (k: number) => 1 - (1 - k) ** 3;

/** Values `k` (0–1) of the way from `from` to `to`. */
export function between(from: readonly number[], to: readonly number[], k: number): number[] {
  return to.map((v, i) => (from[i] ?? v) + (v - (from[i] ?? v)) * k);
}

const toKey = (values: readonly number[]) => values.join(',');
const fromKey = (key: string) => (key === '' ? [] : key.split(',').map(Number));

/**
 * Numbers that glide to new values over `ms` instead of jumping, for charts drawn from them. Shows the
 * targets right away when motion is reduced, the count changes, or the browser has no animation frames.
 * Text alternatives should keep using the targets.
 */
export function useTween(values: readonly number[], ms = 300): readonly number[] {
  const key = toKey(values);
  // The frame on screen while gliding towards `key`; null once there (or when not animating).
  const [frame, setFrame] = useState<{ key: string; values: number[] } | null>(null);
  const last = useRef<readonly number[]>(values);

  useEffect(() => {
    const to = fromKey(key);
    const from = last.current;
    if (toKey(from) === key) return undefined;
    if (typeof requestAnimationFrame !== 'function' || from.length !== to.length || prefersReducedMotion()) {
      last.current = to;
      return undefined;
    }
    let id = 0;
    const start = performance.now();
    const step = (time: number) => {
      const k = Math.min(1, (time - start) / ms);
      const next = between(from, to, easeOut(k));
      last.current = next;
      setFrame(k < 1 ? { key, values: next } : null);
      if (k < 1) id = requestAnimationFrame(step);
    };
    id = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(id);
    };
  }, [key, ms]);

  return frame?.key === key ? frame.values : values;
}
